import * as ROUTES from '@account/constants/routes';
import MenuIcon from '@mui/icons-material/Menu';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef} from 'react';
import RouterIcon from '@mui/icons-material/Router';
import {API_TYPE} from '@account/config';
import {Box, Chip, Hidden, IconButton} from '@mui/material';
import {DARK_THEME} from '@apps-common/ui-theme/Themes';
import {NavBar} from '@apps-common/ui-components';
import {ThemeProvider} from '@emotion/react';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {useAuth} from '@apps-common/auth-react';
import {useFlags} from '@hooks/useFlags';
import {useHistory} from 'react-router-dom';
import {useUIMode} from '@hooks/useUIMode';

const getApiInfo = () => {
  const color = {
    live: 'success',
    next: 'info',
    local: 'error',
    edition: 'warning',
  }[API_TYPE];

  return {
    icon: <RouterIcon />,
    label: API_TYPE,
    color,
    sx: {
      backgroundColor: theme => theme.palette[color].dark,
      color: theme => theme.palette[color].light,
    },
  };
};

const AppBar = ({onMenuClick}) => {
  const auth = useAuth();
  const history = useHistory();
  const {isNewUI, toggle} = useUIMode();

  // Holds the isNewUI value at the moment a toggle is requested (user click or
  // the force-toggle effect below). The redirect effect only fires once the
  // mode has actually flipped away from this snapshot, so there's a single
  // navigation per mode change - no mount redirect and no setTimeout race.
  const redirectFromRef = useRef(null);

  const handleToggle = useCallback(() => {
    redirectFromRef.current = isNewUI;
    toggle();
  }, [isNewUI, toggle]);

  const isPlaneteer = hasRoleLevel(auth, [
    roleLevels.planet_user,
    roleLevels.planet_admin,
  ]);
  const {newUiToggle} = useFlags();
  const canToggleUI =
    hasRoleLevel(auth, [roleLevels.planet_admin]) && newUiToggle;

  // Force Classic UI only when New-UI access is LOST mid-session (the flag was
  // turned off) — i.e. canToggleUI transitions true -> false. NOT on initial
  // load: LaunchDarkly boots on an anonymous context, so a user-targeted flag
  // reads false until identify() runs post-auth. The old guard
  // (`newUiToggle === undefined`) never fired because useFlags coerces the flag
  // to a boolean, so a cold deep-link to a New-UI route force-reverted New-UI
  // users to Classic and redirected them to /organizations before the flag
  // resolved.
  const prevCanToggleRef = useRef(canToggleUI);
  useEffect(() => {
    const wasAbleToToggle = prevCanToggleRef.current;
    prevCanToggleRef.current = canToggleUI;
    if (wasAbleToToggle && !canToggleUI && isNewUI) {
      redirectFromRef.current = isNewUI;
      toggle();
    }
  }, [canToggleUI, isNewUI, toggle]);

  // Perform the redirect once the UI mode has actually changed. Keying on the
  // post-flip isNewUI value (rather than a deferred replace inside
  // handleToggle) means one navigation per mode change with no race.
  useEffect(() => {
    if (redirectFromRef.current === null) return;
    if (redirectFromRef.current === isNewUI) return;
    redirectFromRef.current = null;
    history.replace(isNewUI ? ROUTES.ACCOUNTS : ROUTES.ORGS);
  }, [isNewUI, history]);

  return (
    <ThemeProvider theme={DARK_THEME}>
      <NavBar
        title="Account"
        {...(API_TYPE !== 'live' && isPlaneteer
          ? {titleChipProps: getApiInfo()}
          : {})}
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <Hidden smUp>
              <Box marginLeft={1}>
                <IconButton
                  onClick={onMenuClick}
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  size="small"
                  style={{padding: 0}}
                  data-qe="applications-menu"
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Hidden>
            {canToggleUI && (
              <Chip
                label={isNewUI ? 'Switch to Classic' : 'Switch to New UI'}
                size="small"
                onClick={handleToggle}
                sx={{
                  cursor: 'pointer',
                  bgcolor: isNewUI ? 'success.dark' : 'grey.700',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                  '&:hover': {
                    bgcolor: isNewUI ? 'success.main' : 'grey.600',
                  },
                }}
              />
            )}
          </Box>
        }
        user={auth.user?.claims}
        logout={auth.signOut}
        userMenuProps={{showAccountLink: false}}
        sx={{zIndex: theme => theme.zIndex.drawer + 1}}
      />
    </ThemeProvider>
  );
};
AppBar.propTypes = {
  onMenuClick: PropTypes.func,
};

export default AppBar;
